import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { PermissionsEnum } from "@/auth/interfaces";
import {
  hasAllPermissions,
  hasPermission,
  useCheckPermissions,
  useTableFilter,
} from "@/shared";
import {
  useFetchCargos,
  useUpdateCargos,
} from "@/shared/actions/erp/empleados";
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from "@/shared/components/ui";
import { Cargos } from "@/shared/interfaces/erp/empleados";
import { useUiConfirmModalStore } from "@/store/ui";

const returnUrlCargos = "/nomina/cargos";

export interface CargosPageInterface {}

const CargosPage: React.FC<CargosPageInterface> = () => {
  useCheckPermissions(PermissionsEnum.api_view_cargo);

  const navigate = useNavigate();

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex } = pagination;

  ///* fetch data
  const {
    data: CargosPagingRes,
    isLoading,
    isRefetching,
  } = useFetchCargos({
    page: pageIndex + 1,
    nombre: searchTerm,
  });

  ///* handlers
  const onEdit = (original: Cargos) => {
    navigate(`${returnUrlCargos}/editar/${original.id_cargo}`);
  };
  ///* global state
  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    (s) => s.setConfirmDialogIsOpen,
  );
  const updateCargos = useUpdateCargos({
    navigate,
    returnUrl: returnUrlCargos,
  });
  ///* columns
  const columns = useMemo<MRT_ColumnDef<Cargos>[]>(
    () => [
      {
        accessorKey: "codigo",
        header: "Codigo",
        size: 300,
      },
      {
        accessorKey: "numero_referencia",
        header: "Numero referencia",
        size: 300,
      },
      {
        accessorKey: "nombre",
        header: "Nombre",
        size: 300,
      },
      {
        accessorKey: "tipo_empleado",
        header: "tipo de empleados",
        size: 300,
      },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <SwitchComponente
            estadoSwitch={row.original.estado}
            titulo={row.original.estado ? "Activo" : "Inactivo"}
            setEstadoSwitch={(cargo) => {
              if (!hasPermission(PermissionsEnum.api_change_cargo)) return;
              setConfirmDialog({
                isOpen: true,
                title: "Cambiar Estado",
                subtitle:
                  "¿Está seguro que desea cambiar el estado de este registro ?",
                onConfirm: () => {
                  setConfirmDialogIsOpen(false);
                  updateCargos.mutate({
                    id: row.original.id_cargo!,
                    data: {
                      ...row.original,
                      estado: cargo as boolean,
                    },
                  });
                },
              });
            }}
          />
        ),
      },
    ],
    [],
  );

  return (
    <SingleTableBoxScene
      title="Cargos"
      urlRedirect={`${returnUrlCargos}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_cargo)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="Cargos"
      />

      <GeneralTable<Cargos>
        columns={columns}
        data={CargosPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={CargosPagingRes?.data?.count}
        // // actions
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_cargo,
        ])}
        // crud
        onEdit={onEdit}
        canEdit={hasAllPermissions([PermissionsEnum.api_change_cargo])}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default CargosPage;
